import React, { useState } from 'react';
import { Badge, Button, Dropdown, Layout, Menu, MenuProps } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  DashboardOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  ExportOutlined,
  ImportOutlined,
  CreditCardOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {
  UserCircleIcon,
  CircleStackIcon,
  BanknotesIcon,
} from '@heroicons/react/24/solid';
import { EPermission } from '../enums/EPermission';
import {
  DocumentCheckIcon,
  UserPlusIcon,
  Bars4Icon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { EUserRole } from '@/enums/EUserRole';
import { mainMenu } from '@/configs/menuItems';
import { AxiosResponse } from 'axios';
import { useQuery } from '@tanstack/react-query';
import { IInboundCount } from '@/interfaces/inbound';
import { countInbounds } from '@/adapters/inbound';
import { EInboundType } from '@/enums/EInbound';
import { searchLocationCheckingReport } from '@/adapters/report';
import { isObServe } from '@/utils/permissionUtils';
import { ILocationCheckingRes } from '@/interfaces/location';

const { Header, Content } = Layout;

const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const navigate = useNavigate();
  const { logout, userInfo } = useAuth();
  type MenuItem = Required<MenuProps>['items'][number] & {
    permission?: EPermission;
    roles?: EUserRole[];
  };

  const dashbaordMenu = mainMenu.find(c => c.key === 'dashboard');
  const orderMenu = mainMenu.find(c => c.key === 'order');
  const exportMenu = mainMenu.find(c => c.key === 'export');
  const customerMenu = mainMenu.find(c => c.key === 'customer');
  const productMenu = mainMenu.find(c => c.key === 'product');
  const stockMenu = mainMenu.find(c => c.key === 'stock');
  const collectorMenu = mainMenu.find(c => c.key === 'collector');
  const stockReportMenu = mainMenu.find(c => c.key === 'stock-report');
  const companyReportMenu = mainMenu.find(c => c.key === 'company');
  const inboundMenu = mainMenu.find(c => c.key === 'inbound');
  const returnMenu = mainMenu.find(c => c.key === 'return');
  const locationMenu = mainMenu.find(c => c.key === 'location');
  const cnDnMenu = mainMenu.find(c => c.key === 'cndn-note');
  const billingNoteMenu = mainMenu.find(c => c.key === 'billing-note');

  const { data: countData } = useQuery<AxiosResponse<IInboundCount>, Error>({
    queryKey: ['count-inbound'],
    queryFn: async () => await countInbounds(EInboundType.NORMAL),
  });

  const { data: counReturn } = useQuery<AxiosResponse<IInboundCount>, Error>({
    queryKey: ['count-inbound-return'],
    queryFn: async () => await countInbounds(EInboundType.RETURN),
  });

  const { data: locationCount } = useQuery<
    AxiosResponse<ILocationCheckingRes[]>,
    Error
  >({
    queryKey: ['search-location-checking', { isLocationUpdate: false }],
    queryFn: async () =>
      await searchLocationCheckingReport({ isLocationUpdate: false }),
    enabled: !isObServe(),
  });

  const items: MenuItem[] = [
    {
      label: 'ภาพรวม',
      key: 'dashboard',
      icon: <DashboardOutlined />,
      permission: EPermission.Allow,
      roles: dashbaordMenu?.roles || [],
    },
    {
      label: 'ออเดอร์',
      key: 'order',
      icon: <Bars4Icon className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: orderMenu?.roles || [],
    },
    {
      label: exportMenu?.name,
      key: exportMenu?.key || '',
      icon: <ExportOutlined className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: orderMenu?.roles || [],
    },
    {
      label: (
        <>
          {`${locationMenu?.name}`}
          <Badge className="ml-2" count={locationCount?.data?.length || 0} />
        </>
      ),
      key: locationMenu?.key || '',
      icon: <EnvironmentOutlined className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: locationMenu?.roles || [],
    },
    {
      label: (
        <>
          {`${inboundMenu?.name}`}
          <Badge
            className="ml-2"
            count={
              (countData?.data?.error || 0) + (countData?.data?.sendError || 0)
            }
          />
        </>
      ),
      key: inboundMenu?.key || '',
      icon: <ImportOutlined className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: inboundMenu?.roles || [],
    },
    {
      label: (
        <>
          {`${returnMenu?.name}`}
          <Badge
            className="ml-2"
            count={
              (counReturn?.data?.error || 0) +
              (counReturn?.data?.sendError || 0)
            }
          />
        </>
      ),
      key: returnMenu?.key || '',
      icon: <ImportOutlined className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: returnMenu?.roles || [],
    },
    {
      label: 'ลูกค้า',
      key: 'customer',
      icon: <UserPlusIcon className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: customerMenu?.roles || [],
    },
    {
      label: 'สินค้า',
      key: 'product',
      icon: <CircleStackIcon className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: productMenu?.roles || [],
    },
    {
      label: 'สต็อก',
      key: 'stock',
      icon: <Squares2X2Icon className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: stockMenu?.roles || [],
    },
    {
      label: 'ใบเพิ่มหนี้/ ลดหนี้',
      key: 'cndn-note',
      icon: <CreditCardOutlined />,
      permission: EPermission.Allow,
      roles: cnDnMenu?.roles || [],
    },
    {
      label: 'ใบวางบิล',
      key: 'billing-note',
      icon: <FileTextOutlined />,
      permission: EPermission.Allow,
      roles: billingNoteMenu?.roles || [],
    },
    {
      label: 'Free Form',
      key: 'free-form',
      icon: <FileTextOutlined />,
      permission: EPermission.Allow,
      roles: cnDnMenu?.roles || [],
    },
    {
      label: 'Collector',
      key: 'collector',
      icon: <BanknotesIcon className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: collectorMenu?.roles || [],
    },
    {
      label: 'Collector Report',
      key: 'collector-report',
      icon: <DocumentCheckIcon className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: [EUserRole.Collector],
    },
    {
      label: 'รายงาน',
      key: 'stock-report',
      icon: <DocumentCheckIcon className="w-4 h-4" />,
      permission: EPermission.Allow,
      roles: stockReportMenu?.roles || [],
    },
    {
      label: 'ตั้งค่า',
      key: 'company',
      icon: <SettingOutlined />,
      permission: EPermission.Allow,
      roles: companyReportMenu?.roles || [],
      className: 'hover: hover:text-black font-bold',
    },
  ];

  const [current, setCurrent] = useState('mail');

  const onClick: MenuProps['onClick'] = e => {
    setCurrent(e.key);
    navigate(`/${e.key}`);
  };

  const userIitems: MenuProps['items'] = [
    {
      key: 'logout',
      label: 'ออกจากระบบ',
      icon: <LogoutOutlined />,
      onClick: () => {
        logout();
        navigate('/login');
      },
    },
  ];

  const filteredItems = items.filter(item =>
    item.roles?.includes(userInfo?.role || EUserRole.Admin),
  );

  return (
    <Layout style={{ minHeight: '100vh' }} className="font-ibm">
      <Header
        className="pl-2 pr-4 w-full"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          width: '100%',
          alignItems: 'center',
          minHeight: 65,
        }}
        // style={{ backgroundColor: '#0a35c3' }}
      >
        <div className="flex gap-3 flex-nowrap overflow-y-auto items-center md:justify-between">
          <Menu
            onClick={onClick}
            selectedKeys={[current]}
            mode="horizontal"
            items={filteredItems}
            theme="dark"
            className="flex flex-row"
            overflowedIndicator={<MenuUnfoldOutlined />}
            disabledOverflow={true}
          />
          <div className="flex flex-row justify-center items-center gap-2">
            <div className="flex text-white"> v1.1.3</div>
            <div className="flex">
              <Dropdown
                menu={{ items: userIitems }}
                placement="bottomLeft"
                arrow
                className="font-ibm"
              >
                <Button type="text" className="text-white font-bold">
                  <UserCircleIcon className="w-5 h-5" /> {userInfo?.name}
                </Button>
              </Dropdown>
            </div>
          </div>
        </div>
      </Header>
      <Layout
        style={{
          backgroundImage:
            'linear-gradient(rgba(245, 245, 245, 0.7), rgba(245, 245, 245, 0.6)), url(/bg-01.webp)',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
        }}
      >
        <Content className="mb-2">{children}</Content>
      </Layout>
    </Layout>
  );
};

export default AppLayout;
